import type {
  EmailDeliveryIssue,
  EmailDeliveryIssueProvider,
  EmailDeliveryIssueReason,
  LinkedProjectEntry,
  Organization,
  PendingRosterUpdate,
  RoleTemplate,
  RosterCategory,
  RosterPerson,
  RosterWorkEntry,
} from '@/features/admin/org-types';
import { db } from '@/firebase-config';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';

import type {
  OrganizationAdminClient,
  RosterResolutionCandidate,
  RosterResolutionRequest,
} from '../contracts';
import {
  isRecord,
  optionalString,
  readArray,
  readString,
  timestampDate,
} from './firestore-read-normalizers';

type ReadDocumentSnapshot = {
  id: string;
  data: () => unknown;
  exists?: () => boolean;
};

type QuerySnapshotLike = {
  docs: ReadDocumentSnapshot[];
  size?: number;
};

const ORG_COLLECTION = 'organizations';
const ROSTER_COLLECTION = 'roster';
const EMAIL_DELIVERY_ISSUES_COLLECTION = 'emailDeliveryIssues';
const ROLE_TEMPLATES_COLLECTION = 'roleTemplates';
const PENDING_UPDATES_COLLECTION = 'pendingUpdates';
const ROSTER_RESOLUTION_REQUESTS_COLLECTION = 'rosterResolutionRequests';

const ROSTER_CATEGORIES = new Set<RosterCategory>([
  'staff',
  'volunteer',
  'board',
  'student',
  'guest',
  'contractor',
]);

function dateOrNow(value: unknown): Date {
  return timestampDate(value) ?? new Date();
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function stringList(value: unknown): string[] {
  return readArray(value, isString)
    .map((item) => item.trim())
    .filter(Boolean);
}

function recordList<T>(value: unknown, mapper: (record: Record<string, unknown>) => T | null): T[] {
  return Array.isArray(value)
    ? value.flatMap((item) => {
        if (!isRecord(item)) return [];
        const mapped = mapper(item);
        return mapped ? [mapped] : [];
      })
    : [];
}

function optionalRosterCategory(value: unknown): RosterCategory | null {
  return typeof value === 'string' && ROSTER_CATEGORIES.has(value as RosterCategory)
    ? value as RosterCategory
    : null;
}

function optionalInventoryCheckoutPolicy(value: unknown): Organization['inventoryCheckoutPolicy'] {
  return value === 'open' || value === 'managed' ? value : undefined;
}

function optionalPendingStatus(value: unknown): PendingRosterUpdate['status'] {
  return value === 'accepted' || value === 'dismissed' || value === 'pending'
    ? value
    : 'pending';
}

function optionalEmailDeliveryIssueReason(value: unknown): EmailDeliveryIssueReason | null {
  return value === 'hard-bounce' ||
    value === 'spam-complaint' ||
    value === 'provider-suppression'
    ? value
    : null;
}

function optionalEmailDeliveryIssueProvider(value: unknown): EmailDeliveryIssueProvider | null {
  return value === 'postmark' ? value : null;
}

function toOrganization(id: string, rawData: unknown): Organization {
  const data = isRecord(rawData) ? rawData : {};
  return {
    id,
    name: readString(data.name),
    logo: optionalString(data.logo),
    createdAt: dateOrNow(data.createdAt),
    updatedAt: dateOrNow(data.updatedAt),
    ownerId: readString(data.ownerId),
    inventoryCheckoutPolicy: optionalInventoryCheckoutPolicy(data.inventoryCheckoutPolicy),
  };
}

function toRosterWorkEntry(data: Record<string, unknown>): RosterWorkEntry | null {
  const projectId = optionalString(data.projectId);
  const projectTitle = optionalString(data.projectTitle);
  const role = optionalString(data.role);
  if (!projectId || !projectTitle || !role) return null;

  return {
    projectId,
    projectTitle,
    role,
    startDate: optionalString(data.startDate),
    endDate: optionalString(data.endDate),
  };
}

function toLinkedProjectEntry(data: Record<string, unknown>): LinkedProjectEntry | null {
  const projectId = optionalString(data.projectId);
  const personnelId = optionalString(data.personnelId);
  const projectTitle = optionalString(data.projectTitle);
  if (!projectId || !personnelId || !projectTitle) return null;

  return {
    projectId,
    personnelId,
    projectTitle,
  };
}

function toRosterPerson(docSnap: ReadDocumentSnapshot): RosterPerson {
  const data = isRecord(docSnap.data()) ? docSnap.data() as Record<string, unknown> : {};
  return {
    id: docSnap.id,
    name: readString(data.name),
    givenName: optionalString(data.givenName),
    familyName: optionalString(data.familyName),
    sortName: optionalString(data.sortName),
    pronouns: optionalString(data.pronouns),
    email: optionalString(data.email),
    phone: optionalString(data.phone),
    emergencyName: optionalString(data.emergencyName),
    emergencyPhone: optionalString(data.emergencyPhone),
    category: optionalRosterCategory(data.category) ?? 'staff',
    headshotUrl: optionalString(data.headshotUrl),
    bio: optionalString(data.bio),
    notes: optionalString(data.notes),
    workHistory: recordList(data.workHistory, toRosterWorkEntry),
    linkedProjects: recordList(data.linkedProjects, toLinkedProjectEntry),
    externalSource: optionalString(data.externalSource) ?? 'manual',
    locallyModified: data.locallyModified === true,
    createdAt: dateOrNow(data.createdAt),
    updatedAt: dateOrNow(data.updatedAt),
  };
}

function toEmailDeliveryIssue(docSnap: ReadDocumentSnapshot): EmailDeliveryIssue | null {
  const data = isRecord(docSnap.data()) ? docSnap.data() as Record<string, unknown> : {};
  const email = optionalString(data.email);
  const reason = optionalEmailDeliveryIssueReason(data.reason);
  const provider = optionalEmailDeliveryIssueProvider(data.provider);
  if (!email || data.status !== 'active' || !reason || !provider) {
    return null;
  }

  return {
    id: docSnap.id,
    email,
    status: 'active',
    reason,
    provider,
    providerMessageId: optionalString(data.providerMessageId),
    detail: optionalString(data.detail),
    firstSeenAt: optionalString(data.firstSeenAt) ?? '',
    lastSeenAt: optionalString(data.lastSeenAt) ?? '',
    clearedAt: optionalString(data.clearedAt),
    clearedBy: optionalString(data.clearedBy),
  };
}

function toRoleTemplate(docSnap: ReadDocumentSnapshot): RoleTemplate {
  const data = isRecord(docSnap.data()) ? docSnap.data() as Record<string, unknown> : {};
  return {
    id: docSnap.id,
    name: readString(data.name),
    description: readString(data.description),
    permissions: stringList(data.permissions),
    isDefault: data.isDefault === true,
    createdAt: dateOrNow(data.createdAt),
    updatedAt: dateOrNow(data.updatedAt),
  };
}

function toPendingRosterUpdate(docSnap: ReadDocumentSnapshot): PendingRosterUpdate {
  const data = isRecord(docSnap.data()) ? docSnap.data() as Record<string, unknown> : {};
  return {
    id: docSnap.id,
    rosterPersonId: readString(data.rosterPersonId),
    field: readString(data.field),
    oldValue: readString(data.oldValue),
    newValue: readString(data.newValue),
    sourceProjectId: readString(data.sourceProjectId),
    sourceProjectTitle: readString(data.sourceProjectTitle),
    sourcePersonnelId: readString(data.sourcePersonnelId),
    updatedBy: readString(data.updatedBy),
    createdAt: dateOrNow(data.createdAt),
    status: optionalPendingStatus(data.status),
  };
}

function toPersonnelSnapshot(value: unknown): RosterResolutionRequest['personnelSnapshot'] {
  if (!isRecord(value)) return;
  return {
    name: optionalString(value.name),
    email: optionalString(value.email),
    category: optionalString(value.category),
  };
}

function toRosterResolutionCandidate(
  data: Record<string, unknown>,
): RosterResolutionCandidate | null {
  const id = optionalString(data.id);
  if (!id) return null;

  return {
    id,
    name: optionalString(data.name),
    email: optionalString(data.email),
    matchReasons: stringList(data.matchReasons),
  };
}

function toRosterResolutionRequest(docSnap: ReadDocumentSnapshot): RosterResolutionRequest | null {
  const data = isRecord(docSnap.data()) ? docSnap.data() as Record<string, unknown> : {};
  if (data.status !== 'open') return null;

  return {
    id: docSnap.id,
    projectTitle: optionalString(data.projectTitle),
    personnelSnapshot: toPersonnelSnapshot(data.personnelSnapshot),
    candidates: recordList(data.candidates, toRosterResolutionCandidate),
    matchReasons: stringList(data.matchReasons),
  };
}

export class FirebaseOrganizationAdminClient implements OrganizationAdminClient {
  async getOrganization(orgId: string): Promise<Organization | null> {
    const snapshot = await getDoc(doc(db, ORG_COLLECTION, orgId)) as ReadDocumentSnapshot;
    if (snapshot.exists && !snapshot.exists()) return null;
    return toOrganization(snapshot.id || orgId, snapshot.data());
  }

  async listRoster(orgId: string): Promise<RosterPerson[]> {
    const snapshot = await getDocs(
      collection(db, ORG_COLLECTION, orgId, ROSTER_COLLECTION),
    ) as QuerySnapshotLike;
    return snapshot.docs.map(toRosterPerson);
  }

  async listEmailDeliveryIssues(orgId: string): Promise<EmailDeliveryIssue[]> {
    const snapshot = await getDocs(
      query(
        collection(db, ORG_COLLECTION, orgId, EMAIL_DELIVERY_ISSUES_COLLECTION),
        where('status', '==', 'active'),
      ),
    ) as QuerySnapshotLike;

    return snapshot.docs.flatMap((docSnap) => {
      const issue = toEmailDeliveryIssue(docSnap);
      return issue ? [issue] : [];
    });
  }

  async listRoleTemplates(orgId: string): Promise<RoleTemplate[]> {
    const snapshot = await getDocs(
      collection(db, ORG_COLLECTION, orgId, ROLE_TEMPLATES_COLLECTION),
    ) as QuerySnapshotLike;
    return snapshot.docs.map(toRoleTemplate);
  }

  async listPendingRosterUpdates(orgId: string): Promise<PendingRosterUpdate[]> {
    const snapshot = await this.getPendingRosterUpdatesSnapshot(orgId);
    return snapshot.docs.map(toPendingRosterUpdate);
  }

  async countPendingRosterUpdates(orgId: string): Promise<number> {
    const snapshot = await this.getPendingRosterUpdatesSnapshot(orgId);
    return snapshot.size ?? snapshot.docs.length;
  }

  async listRosterResolutionRequests(orgId: string): Promise<RosterResolutionRequest[]> {
    const snapshot = await getDocs(
      query(
        collection(db, ORG_COLLECTION, orgId, ROSTER_RESOLUTION_REQUESTS_COLLECTION),
        where('status', '==', 'open'),
      ),
    ) as QuerySnapshotLike;

    return snapshot.docs.flatMap((docSnap) => {
      const request = toRosterResolutionRequest(docSnap);
      return request ? [request] : [];
    });
  }

  private async getPendingRosterUpdatesSnapshot(orgId: string): Promise<QuerySnapshotLike> {
    return await getDocs(
      query(
        collection(db, ORG_COLLECTION, orgId, PENDING_UPDATES_COLLECTION),
        where('status', '==', 'pending'),
      ),
    ) as QuerySnapshotLike;
  }
}
